import React, { useState, useEffect } from 'react';
import { scheduleService } from '../services';
import { DataTable } from '../components';
import { useSideDialog, useModalDialog, useMessage, useInterval } from '../hooks';
import { formatData, handleError, cronDescription, getTimezoneAbbreviation } from '../helpers';

/**
 * Schedules management view.
 * Lists all cron schedules, supports add/edit/delete via side dialog and modal confirmation.
 * Polls every 3 s to reflect next-run timestamp updates.
 */
export const Schedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();

  // Poll every 3 s so next-run timestamps update without manual refresh
  useInterval(() => {
    fetchItems();
  }, 3000);

  useEffect(() => {
    fetchItems();
  }, []);

  /**
   * Fetches all schedules and enriches each row with a human-readable cron description.
   */
  const fetchItems = async () => {
    scheduleService
      .getAll()
      .then(res => {
        if (res?.schedules) {
          let maxTaskCount = 0;
          res.schedules.forEach(s => {
            if (s.task_count > maxTaskCount) maxTaskCount = s.task_count;
          });

          const enriched = res.schedules.map(schedule => {
            const desc = cronDescription(schedule.schedule_cron);
            const abbr = getTimezoneAbbreviation(schedule.schedule_timezone || 'UTC');
            return {
              ...schedule,
              schedule_cron_description: desc ? `${desc} (${abbr})` : desc,
              task_count_max: maxTaskCount,
              schedule_next_run_timestamp: schedule.schedule_is_enabled
                ? formatData(schedule.schedule_next_run_timestamp, 'datetime')
                : 'Disabled',
              schedule_next_run_className: schedule.schedule_is_enabled ? '' : 'small-text',
            };
          });
          setSchedules(enriched);
          setLoading(false);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Submits a new schedule via the API and refreshes the table.
   * @param {Object|Object[]} data - Schedule data from the add form.
   */
  const addSchedule = async data => {
    const payload = { schedules: Array.isArray(data) ? data : [data] };

    await scheduleService
      .create(payload)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  /**
   * Submits schedule edits via the API and refreshes the table.
   * @param {Object|Object[]} data - Schedule data from the edit form.
   */
  const editSchedule = async data => {
    const payload = { schedules: Array.isArray(data) ? data : [data] };

    await scheduleService
      .update(payload)
      .then(res => {
        fetchItems();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  /**
   * Manually enqueues all eligible tasks for the selected schedule(s).
   * Disabled, running, and already-queued tasks are silently skipped by the backend.
   * @param {Object[]} data - Selected schedule objects.
   */
  const runSchedule = async data => {
    const selectedSchedules = data || selectedItems;
    await scheduleService
      .run(selectedSchedules.map(s => s.schedule_id))
      .then(res => res && showMessage(res.message))
      .catch(err => showMessage(err, 'error'));
  };

  /**
   * Deletes selected schedules after the user confirms the impact modal.
   * @param {Object[]} data - Selected schedule objects.
   */
  const deleteSchedule = async data => {
    await scheduleService
      .destroy({ schedules: data })
      .then(res => {
        hideModalDialog();
        clearSelectedItems();
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const scheduleIsEnabledValues = {
    true: { label: 'Enabled', className: 'pill' },
    false: { label: 'Disabled', className: 'pill pill-semifull-red' },
  };

  return (
    <div className="Schedules wide-margin">
      <DataTable
        columns={[
          {
            id: 'schedule_name',
            name: 'Name',
            link: '/schedules/:schedule_id',
            tooltip: 'schedule_description',
            className: 'bold',
          },
          { id: 'schedule_description', name: 'Description', viewMode: 'descriptions' },
          { id: 'schedule_cron_description', name: 'Schedule', className: 'small-text' },
          { id: 'schedule_cron', name: 'Cron', type: 'code', tooltip: 'schedule_cron_description' },
          {
            id: 'task_count',
            name: 'Tasks',
            type: 'chart',
            chartType: 'bar',
            data: { data: 'task_count', max: 'task_count_max' },
            viewMode: 'statistics',
          },
          {
            id: 'schedule_next_run_timestamp',
            name: 'Next Run',
            className: ':schedule_next_run_className',
            viewMode: 'statistics',
          },
          {
            id: 'schedule_last_enqueued_timestamp',
            name: 'Last Run',
            type: 'datetime',
            viewMode: 'statistics',
          },
          {
            id: 'schedule_is_enabled',
            name: 'Status',
            type: 'boolean',
            values: scheduleIsEnabledValues,
          },
        ]}
        buttons={[
          {
            label: '+ Add',
            action: 'add',
            mainButton: true,
            onClick: items => showSideDialog('addSchedule', items || selectedItems, addSchedule),
          },
          {
            label: 'Run',
            action: 'run',
            onClick: runSchedule,
            tooltip: 'Run all tasks assigned to selected schedules',
            disabled: {
              field: 'schedule_is_enabled',
              value: false,
              tooltip: 'All selected schedules are disabled',
            },
          },
          {
            label: 'Edit',
            action: 'edit',
            onClick: items => showSideDialog('editSchedule', items || selectedItems, editSchedule),
          },
          {
            label: 'Delete',
            action: 'delete',
            onClick: items =>
              showModalDialog('deleteSchedule', items || selectedItems, deleteSchedule),
          },
        ]}
        data={schedules}
        title="Schedules"
        idColumn="schedule_id"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        showViewModes={true}
        filterable={true}
        filterObjects={[
          'schedule_name',
          'schedule_description',
          'schedule_cron',
          'schedule_is_enabled',
        ]}
        filterObjectsValueMap={{ schedule_is_enabled: scheduleIsEnabledValues }}
      />
    </div>
  );
};
